import { useState } from 'react';
import { motion } from 'framer-motion';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Separator } from '@/components/ui/separator';
import { Badge } from '@/components/ui/badge';
import { useColors, useFonts, useLayout, useUpdateSettings, useSiteConfig, useUpdateSiteConfig } from '@/hooks/use-site-settings';
import { Palette, Type, Layout as LayoutIcon, Globe, Mail, Phone, Link2, Save, RefreshCw, Loader2, Code, Image as ImageIcon, FileJson } from 'lucide-react';
import { toast } from 'sonner';
import { ImageUpload } from '@/components/ui/image-upload';
import { useUpload } from '@/hooks/use-upload';

export default function AdminSettings() {
  const { data: colors, isLoading: loadingColors } = useColors();
  const { data: fonts, isLoading: loadingFonts } = useFonts();
  const { data: layout, isLoading: loadingLayout } = useLayout();
  const { data: siteConfig, isLoading: loadingConfig } = useSiteConfig();

  const updateSettings = useUpdateSettings();
  const updateSiteConfig = useUpdateSiteConfig();

  const [activeTab, setActiveTab] = useState('colors');
  const [saving, setSaving] = useState(false);
  const { uploadImage, base64ToFile } = useUpload();

  // Local state for colors
  const [colorValues, setColorValues] = useState<Record<string, string>>({});
  const [colorGradients, setColorGradients] = useState<Record<string, string>>({});

  // Local state for fonts
  const [fontValues, setFontValues] = useState<Record<string, string>>({});

  // Local state for layout
  const [layoutValues, setLayoutValues] = useState<Record<string, string>>({});

  // Local state for site config
  const [siteConfigValues, setSiteConfigValues] = useState<Partial<any>>({});

  const handleColorChange = (key: string, value: string) => {
    setColorValues(prev => ({ ...prev, [key]: value }));
  };

  const handleGradientChange = (key: string, value: string) => {
    setColorGradients(prev => ({ ...prev, [key]: value }));
  };

  const handleFontChange = (key: string, value: string) => {
    setFontValues(prev => ({ ...prev, [key]: value }));
  };

  const handleLayoutChange = (key: string, value: string) => {
    setLayoutValues(prev => ({ ...prev, [key]: value }));
  };

  const handleSiteConfigChange = (key: string, value: string) => {
    setSiteConfigValues(prev => ({ ...prev, [key]: value }));
  };

  const saveColors = async () => {
    try {
      setSaving(true);
      const settings = [
        // Solid colors
        ...Object.entries(colorValues).map(([key, value]) => ({
          section: 'colors',
          key,
          value
        })),
        // Gradients
        ...Object.entries(colorGradients).map(([key, value]) => ({
          section: 'colors',
          key,
          value
        }))
      ];

      await updateSettings.mutateAsync(settings);
      toast.success('Cores salvas com sucesso!');
    } catch (error) {
      console.error('Erro ao salvar cores:', error);
      toast.error('Erro ao salvar cores');
    } finally {
      setSaving(false);
    }
  };

  const saveFonts = async () => {
    try {
      setSaving(true);
      const settings = Object.entries(fontValues).map(([key, value]) => ({
        section: 'fonts',
        key,
        value
      }));

      await updateSettings.mutateAsync(settings);
      toast.success('Fontes salvas com sucesso!');
    } catch (error) {
      console.error('Erro ao salvar fontes:', error);
      toast.error('Erro ao salvar fontes');
    } finally {
      setSaving(false);
    }
  };

  const saveLayout = async () => {
    try {
      setSaving(true);
      const settings = Object.entries(layoutValues).map(([key, value]) => ({
        section: 'layout',
        key,
        value
      }));

      await updateSettings.mutateAsync(settings);
      toast.success('Layout salvo com sucesso!');
    } catch (error) {
      console.error('Erro ao salvar layout:', error);
      toast.error('Erro ao salvar layout');
    } finally {
      setSaving(false);
    }
  };

  const saveSiteConfig = async () => {
    try {
      setSaving(true);

      const configToSave = { ...siteConfigValues };

      // Helper to handle upload
      const handleImageField = async (field: string, prefix: string) => {
        if (configToSave[field] && configToSave[field].startsWith('data:')) {
          const file = await base64ToFile(configToSave[field], `${prefix}-${Date.now()}.png`);
          const uploaded = await uploadImage(file, 'images');
          if (uploaded) configToSave[field] = uploaded;
        }
      };

      await handleImageField('logo_url', 'site-logo');
      await handleImageField('favicon_url', 'site-favicon');

      await updateSiteConfig.mutateAsync(configToSave);
      toast.success('Configurações gerais salvas com sucesso!');
    } catch (error) {
      console.error('Erro ao salvar configurações gerais:', error);
      toast.error('Erro ao salvar configurações gerais');
    } finally {
      setSaving(false);
    }
  };

  const resetToDefaults = async () => {
    if (confirm('Deseja realmente redefinir todas as configurações para os valores padrão?')) {
      try {
        setSaving(true);
        // Reset colors
        setColorValues({
          primary: '#1E40AF',
          primary_hover: '#1E3A8A',
          secondary: '#4F46E5',
          accent: '#3B82F6',
          background: '#030712',
          foreground: '#F1F5F9',
          card: '#111827',
          card_hover: '#1F2937',
          muted: '#6B7280',
          muted_foreground: '#9CA3AF',
          border: '#374151',
          input: '#374151',
          ring: '#1E40AF'
        });

        setColorGradients({
          gradient_primary: '#1E40AF,#3B82F6',
          gradient_secondary: '#4F46E5,#6366F1',
          gradient_accent: '#3B82F6,#60A5FA',
          gradient_hero: '#030712,#0C1222,#111827'
        });

        toast.success('Configurações redefinidas para os valores padrão!');
      } catch (error) {
        console.error('Erro ao redefinir:', error);
        toast.error('Erro ao redefinir configurações');
      } finally {
        setSaving(false);
      }
    }
  };

  if (loadingColors || loadingFonts || loadingLayout || loadingConfig) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="h-8 w-8 animate-spin" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="sticky top-0 z-20 bg-background/80 backdrop-blur-md pb-4 pt-2 border-b mb-6 -mx-6 px-6 flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold tracking-tight">Configurações</h1>
          <p className="text-muted-foreground hidden md:block">
            Gerencie cores, fontes, layout e informações gerais do site
          </p>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" onClick={resetToDefaults} disabled={saving}>
            <RefreshCw className="w-4 h-4 mr-2" />
            Redefinir Padrões
          </Button>
        </div>
      </div>

      <Tabs value={activeTab} onValueChange={setActiveTab} className="space-y-6">
        <TabsList className="grid w-full grid-cols-2 lg:grid-cols-4">
          <TabsTrigger value="colors" className="gap-2">
            <Palette className="w-4 h-4" />
            Cores
          </TabsTrigger>
          <TabsTrigger value="fonts" className="gap-2">
            <Type className="w-4 h-4" />
            Fontes
          </TabsTrigger>
          <TabsTrigger value="layout" className="gap-2">
            <LayoutIcon className="w-4 h-4" />
            Layout
          </TabsTrigger>
          <TabsTrigger value="general" className="gap-2">
            <Globe className="w-4 h-4" />
            Geral
          </TabsTrigger>
          <TabsTrigger value="scripts" className="gap-2">
            <Code className="w-4 h-4" />
            Scripts & SEO
          </TabsTrigger>
        </TabsList>

        {/* Cores */}
        <TabsContent value="colors" className="space-y-6">
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Palette className="w-5 h-5" />
                Cores Primárias
              </CardTitle>
              <CardDescription>
                Configure as cores principais do site
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                {[
                  { key: 'primary', label: 'Cor Primária', default: '#1E40AF' },
                  { key: 'primary_hover', label: 'Primária Hover', default: '#1E3A8A' },
                  { key: 'secondary', label: 'Cor Secundária', default: '#4F46E5' },
                  { key: 'accent', label: 'Cor de Destaque', default: '#3B82F6' },
                  { key: 'background', label: 'Fundo', default: '#030712' },
                  { key: 'foreground', label: 'Texto Principal', default: '#F1F5F9' },
                  { key: 'card', label: 'Cartão', default: '#111827' },
                  { key: 'card_hover', label: 'Cartão Hover', default: '#1F2937' },
                  { key: 'muted', label: 'Muted', default: '#6B7280' },
                  { key: 'muted_foreground', label: 'Muted Foreground', default: '#9CA3AF' },
                  { key: 'border', label: 'Borda', default: '#374151' },
                  { key: 'input', label: 'Input', default: '#374151' },
                  { key: 'ring', label: 'Ring Focus', default: '#1E40AF' },
                ].map(({ key, label, default: _default }) => (
                  <div key={key} className="space-y-2">
                    <Label htmlFor={key}>{label}</Label>
                    <div className="flex gap-2">
                      <Input
                        id={key}
                        type="color"
                        className="w-16 h-10 p-0 border-0 cursor-pointer"
                        value={colorValues[key] || colors?.[key] || _default}
                        onChange={(e) => handleColorChange(key, e.target.value)}
                      />
                      <Input
                        type="text"
                        className="flex-1 font-mono text-sm"
                        value={colorValues[key] || colors?.[key] || _default}
                        onChange={(e) => handleColorChange(key, e.target.value)}
                        placeholder={_default}
                      />
                    </div>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardHeader>
              <CardTitle>Gradientes</CardTitle>
              <CardDescription>
                Configure os gradientes do site
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="space-y-4">
                {[
                  { key: 'gradient_primary', label: 'Gradiente Primário', default: '#1E40AF,#3B82F6' },
                  { key: 'gradient_secondary', label: 'Gradiente Secundário', default: '#4F46E5,#6366F1' },
                  { key: 'gradient_accent', label: 'Gradiente de Destaque', default: '#3B82F6,#60A5FA' },
                  { key: 'gradient_hero', label: 'Gradiente Hero', default: '#030712,#0C1222,#111827' },
                ].map(({ key, label, default: _default }) => (
                  <div key={key} className="space-y-2">
                    <Label htmlFor={key}>{label}</Label>
                    <div className="flex gap-2">
                      <Input
                        id={key}
                        type="text"
                        className="flex-1 font-mono text-sm"
                        value={colorGradients[key] || colors?.[key] || _default}
                        onChange={(e) => handleGradientChange(key, e.target.value)}
                        placeholder={_default}
                      />
                      <div
                        className="w-20 h-10 rounded border"
                        style={{
                          background: `linear-gradient(135deg, ${(colorGradients[key] || colors?.[key] || _default).split(',')})`
                        }}
                      />
                    </div>
                    <p className="text-xs text-muted-foreground">
                      Separe as cores com vírgula (ex: #1E40AF,#3B82F6)
                    </p>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>

          <Button onClick={saveColors} disabled={saving} className="w-full">
            {saving ? (
              <>
                <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                Salvando...
              </>
            ) : (
              <>
                <Save className="w-4 h-4 mr-2" />
                Salvar Cores
              </>
            )}
          </Button>
        </TabsContent>

        {/* Fontes */}
        <TabsContent value="fonts" className="space-y-6">
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Type className="w-5 h-5" />
                Fontes
              </CardTitle>
              <CardDescription>
                Configure as fontes e tipografia do site
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-6">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div className="space-y-2">
                  <Label htmlFor="font_family_display">Fonte de Títulos</Label>
                  <Input
                    id="font_family_display"
                    value={fontValues.font_family_display || fonts?.font_family_display || 'Inter, system-ui, sans-serif'}
                    onChange={(e) => handleFontChange('font_family_display', e.target.value)}
                    placeholder="Inter, system-ui, sans-serif"
                  />
                  <p className="text-xs text-muted-foreground">
                    Fonte usada para títulos e headings
                  </p>
                </div>

                <div className="space-y-2">
                  <Label htmlFor="font_family_body">Fonte de Corpo</Label>
                  <Input
                    id="font_family_body"
                    value={fontValues.font_family_body || fonts?.font_family_body || 'Inter, system-ui, sans-serif'}
                    onChange={(e) => handleFontChange('font_family_body', e.target.value)}
                    placeholder="Inter, system-ui, sans-serif"
                  />
                  <p className="text-xs text-muted-foreground">
                    Fonte usada para textos regulares
                  </p>
                </div>
              </div>

              <Separator />

              <div>
                <h3 className="text-lg font-semibold mb-4">Tamanhos de Fonte</h3>
                <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                  {[
                    { key: 'font_size_base', label: 'Base', default: '16' },
                    { key: 'font_size_display_lg', label: 'Display LG', default: '72' },
                    { key: 'font_size_display_md', label: 'Display MD', default: '56' },
                    { key: 'font_size_display_sm', label: 'Display SM', default: '40' },
                  ].map(({ key, label, default: _default }) => (
                    <div key={key} className="space-y-2">
                      <Label htmlFor={key}>{label} (px)</Label>
                      <Input
                        id={key}
                        type="number"
                        value={fontValues[key] || fonts?.[key] || _default}
                        onChange={(e) => handleFontChange(key, e.target.value)}
                        placeholder={_default}
                      />
                    </div>
                  ))}
                </div>
              </div>

              <Separator />

              <div>
                <h3 className="text-lg font-semibold mb-4">Line Heights</h3>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  {[
                    { key: 'line_height_tight', label: 'Tight', default: '1.25' },
                    { key: 'line_height_normal', label: 'Normal', default: '1.5' },
                    { key: 'line_height_relaxed', label: 'Relaxed', default: '1.75' },
                  ].map(({ key, label, default: _default }) => (
                    <div key={key} className="space-y-2">
                      <Label htmlFor={key}>{label}</Label>
                      <Input
                        id={key}
                        type="number"
                        step="0.05"
                        value={fontValues[key] || fonts?.[key] || _default}
                        onChange={(e) => handleFontChange(key, e.target.value)}
                        placeholder={_default}
                      />
                    </div>
                  ))}
                </div>
              </div>
            </CardContent>
          </Card>

          <Button onClick={saveFonts} disabled={saving} className="w-full">
            {saving ? (
              <>
                <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                Salvando...
              </>
            ) : (
              <>
                <Save className="w-4 h-4 mr-2" />
                Salvar Fontes
              </>
            )}
          </Button>
        </TabsContent>

        {/* Layout */}
        <TabsContent value="layout" className="space-y-6">
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <LayoutIcon className="w-5 h-5" />
                Layout
              </CardTitle>
              <CardDescription>
                Configure o espaçamento e layout do site
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-6">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div className="space-y-2">
                  <Label htmlFor="container_width">Largura do Container (px)</Label>
                  <Input
                    id="container_width"
                    type="number"
                    value={layoutValues.container_width || layout?.container_width || '1280'}
                    onChange={(e) => handleLayoutChange('container_width', e.target.value)}
                    placeholder="1280"
                  />
                  <p className="text-xs text-muted-foreground">
                    Largura máxima do container principal
                  </p>
                </div>

                <div className="space-y-2">
                  <Label htmlFor="border_radius">Border Radius (px)</Label>
                  <Input
                    id="border_radius"
                    type="number"
                    value={layoutValues.border_radius || layout?.border_radius || '16'}
                    onChange={(e) => handleLayoutChange('border_radius', e.target.value)}
                    placeholder="16"
                  />
                  <p className="text-xs text-muted-foreground">
                    Arredondamento das bordas dos cards
                  </p>
                </div>

                <div className="space-y-2">
                  <Label htmlFor="spacing_unit">Unidade de Espaçamento (px)</Label>
                  <Input
                    id="spacing_unit"
                    type="number"
                    value={layoutValues.spacing_unit || layout?.spacing_unit || '4'}
                    onChange={(e) => handleLayoutChange('spacing_unit', e.target.value)}
                    placeholder="4"
                  />
                  <p className="text-xs text-muted-foreground">
                    Base para cálculo de espaçamentos
                  </p>
                </div>

                <div className="space-y-2">
                  <Label htmlFor="max_width_content">Largura Máxima de Conteúdo (px)</Label>
                  <Input
                    id="max_width_content"
                    type="number"
                    value={layoutValues.max_width_content || layout?.max_width_content || '768'}
                    onChange={(e) => handleLayoutChange('max_width_content', e.target.value)}
                    placeholder="768"
                  />
                  <p className="text-xs text-muted-foreground">
                    Largura máxima para seções de conteúdo
                  </p>
                </div>
              </div>

              <div className="bg-muted p-4 rounded-lg">
                <h4 className="font-semibold mb-2">Preview do Layout</h4>
                <div className="space-y-2 text-sm">
                  <div className="flex justify-between">
                    <span>Container Width:</span>
                    <Badge variant="outline">{layoutValues.container_width || layout?.container_width || '1280'}px</Badge>
                  </div>
                  <div className="flex justify-between">
                    <span>Border Radius:</span>
                    <Badge variant="outline">{layoutValues.border_radius || layout?.border_radius || '16'}px</Badge>
                  </div>
                  <div className="flex justify-between">
                    <span>Spacing Unit:</span>
                    <Badge variant="outline">{layoutValues.spacing_unit || layout?.spacing_unit || '4'}px</Badge>
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>

          <Button onClick={saveLayout} disabled={saving} className="w-full">
            {saving ? (
              <>
                <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                Salvando...
              </>
            ) : (
              <>
                <Save className="w-4 h-4 mr-2" />
                Salvar Layout
              </>
            )}
          </Button>
        </TabsContent>

        {/* Geral */}
        <TabsContent value="general" className="space-y-6">
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Globe className="w-5 h-5" />
                Informações Gerais
              </CardTitle>
              <CardDescription>
                Configure as informações básicas e identidade visual do site
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-6">
              <div className="space-y-2">
                <Label htmlFor="site_name">Nome do Site</Label>
                <Input
                  id="site_name"
                  value={siteConfigValues.site_name || siteConfig?.site_name || 'Cássio Lemos - Portfólio 2026'}
                  onChange={(e) => handleSiteConfigChange('site_name', e.target.value)}
                  placeholder="Cássio Lemos - Portfólio 2026"
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="site_description">Descrição do Site</Label>
                <Textarea
                  id="site_description"
                  value={siteConfigValues.site_description || siteConfig?.site_description || ''}
                  onChange={(e) => handleSiteConfigChange('site_description', e.target.value)}
                  placeholder="Descrição breve do site"
                  rows={3}
                />
              </div>

              <Separator />

              <div>
                <h3 className="text-lg font-semibold mb-4 flex items-center gap-2">
                  <ImageIcon className="w-5 h-5" />
                  Identidade Visual
                </h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div className="space-y-2">
                    <ImageUpload
                      label="Logo Principal"
                      value={siteConfigValues.logo_url || siteConfig?.logo_url || ''}
                      onChange={(value) => handleSiteConfigChange('logo_url', value)}
                    />
                    <p className="text-xs text-muted-foreground">Exibido no header</p>
                  </div>
                  <div className="space-y-2">
                    <ImageUpload
                      label="Favicon"
                      value={siteConfigValues.favicon_url || siteConfig?.favicon_url || ''}
                      onChange={(value) => handleSiteConfigChange('favicon_url', value)}
                    />
                    <p className="text-xs text-muted-foreground">Ícone da aba do navegador (32x32px)</p>
                  </div>
                </div>
              </div>

              <Separator />

              <div>
                <h3 className="text-lg font-semibold mb-4 flex items-center gap-2">
                  <Mail className="w-5 h-5" />
                  Contato
                </h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label htmlFor="contact_email">Email</Label>
                    <Input
                      id="contact_email"
                      type="email"
                      value={siteConfigValues.contact_email || siteConfig?.contact_email || ''}
                      onChange={(e) => handleSiteConfigChange('contact_email', e.target.value)}
                      placeholder="email@exemplo.com"
                    />
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="contact_phone">Telefone</Label>
                    <Input
                      id="contact_phone"
                      type="tel"
                      value={siteConfigValues.contact_phone || siteConfig?.contact_phone || ''}
                      onChange={(e) => handleSiteConfigChange('contact_phone', e.target.value)}
                      placeholder="+55 (11) 99999-9999"
                    />
                  </div>
                </div>
              </div>

              <Separator />

              <div>
                <h3 className="text-lg font-semibold mb-4 flex items-center gap-2">
                  <Link2 className="w-5 h-5" />
                  Redes Sociais
                </h3>
                <div className="space-y-4">
                  {[
                    { key: 'linkedin', label: 'LinkedIn', placeholder: 'https://linkedin.com/in/usuario' },
                    { key: 'github', label: 'GitHub', placeholder: 'https://github.com/usuario' },
                    { key: 'twitter', label: 'Twitter/X', placeholder: 'https://twitter.com/usuario' },
                    { key: 'instagram', label: 'Instagram', placeholder: 'https://instagram.com/usuario' },
                    { key: 'dribbble', label: 'Dribbble', placeholder: 'https://dribbble.com/usuario' },
                    { key: 'behance', label: 'Behance', placeholder: 'https://behance.net/usuario' },
                  ].map(({ key, label, placeholder }) => (
                    <div key={key} className="space-y-2">
                      <Label htmlFor={key}>{label}</Label>
                      <Input
                        id={key}
                        type="url"
                        value={siteConfigValues.social_links?.[key] || siteConfig?.social_links?.[key] || ''}
                        onChange={(e) => {
                          const socialLinks = { ...(siteConfigValues.social_links || siteConfig?.social_links || {}), [key]: e.target.value };
                          handleSiteConfigChange('social_links', JSON.stringify(socialLinks));
                        }}
                        placeholder={placeholder}
                      />
                    </div>
                  ))}
                </div>
              </div>
            </CardContent>
          </Card>

          <Button onClick={saveSiteConfig} disabled={saving} className="w-full">
            {saving ? (
              <>
                <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                Salvando...
              </>
            ) : (
              <>
                <Save className="w-4 h-4 mr-2" />
                Salvar Configurações
              </>
            )}
          </Button>
        </TabsContent>

        {/* Scripts & SEO */}
        <TabsContent value="scripts" className="space-y-6">
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <FileJson className="w-5 h-5" />
                SEO
              </CardTitle>
              <CardDescription>
                Otimização para motores de busca
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="seo_title">Título SEO Padrão</Label>
                <Input
                  id="seo_title"
                  value={siteConfigValues.seo_title || siteConfig?.seo_title || ''}
                  onChange={(e) => handleSiteConfigChange('seo_title', e.target.value)}
                  placeholder="Título para motores de busca"
                />
                <p className="text-xs text-muted-foreground">
                  Recomendado: 50-60 caracteres
                </p>
              </div>

              <div className="space-y-2">
                <Label htmlFor="seo_description">Descrição SEO Padrão</Label>
                <Textarea
                  id="seo_description"
                  value={siteConfigValues.seo_description || siteConfig?.seo_description || ''}
                  onChange={(e) => handleSiteConfigChange('seo_description', e.target.value)}
                  placeholder="Descrição para motores de busca"
                  rows={3}
                />
                <p className="text-xs text-muted-foreground">
                  Recomendado: 150-160 caracteres
                </p>
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Code className="w-5 h-5" />
                Scripts Externos
              </CardTitle>
              <CardDescription>
                Adicione scripts customizados (Google Analytics, Pixels, etc)
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-6">
              <div className="space-y-2">
                <Label className="font-mono text-sm">&lt;head&gt; Scripts</Label>
                <Textarea
                  rows={5}
                  className="font-mono text-xs bg-slate-950 text-slate-300"
                  placeholder="<!-- Cole aqui scripts para o head (ex: GTM, Meta Pixel) -->"
                  value={siteConfigValues.scripts_header || siteConfig?.scripts_header || ''}
                  onChange={(e) => handleSiteConfigChange('scripts_header', e.target.value)}
                />
              </div>
              <div className="space-y-2">
                <Label className="font-mono text-sm">&lt;body&gt; Start Scripts</Label>
                <Textarea
                  rows={5}
                  className="font-mono text-xs bg-slate-950 text-slate-300"
                  placeholder="<!-- Cole aqui scripts para o início do body (ex: GTM noscript) -->"
                  value={siteConfigValues.scripts_body_start || siteConfig?.scripts_body_start || ''}
                  onChange={(e) => handleSiteConfigChange('scripts_body_start', e.target.value)}
                />
              </div>
              <div className="space-y-2">
                <Label className="font-mono text-sm">Footer Scripts</Label>
                <Textarea
                  rows={5}
                  className="font-mono text-xs bg-slate-950 text-slate-300"
                  placeholder="<!-- Cole aqui scripts para o final da página (ex: Chat widgets) -->"
                  value={siteConfigValues.scripts_footer || siteConfig?.scripts_footer || ''}
                  onChange={(e) => handleSiteConfigChange('scripts_footer', e.target.value)}
                />
              </div>
            </CardContent>
          </Card>

          <Button onClick={saveSiteConfig} disabled={saving} className="w-full">
            {saving ? (
              <>
                <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                Salvando...
              </>
            ) : (
              <>
                <Save className="w-4 h-4 mr-2" />
                Salvar Configurações
              </>
            )}
          </Button>

        </TabsContent>
      </Tabs>
    </div>
  );
}
